/**
 * Rules Query Policy
 *
 * Responsibilities:
 * - Fetch ALL rules on store init (layout mount), INCLUDING `kind: 'bypass'` rows
 * - Dispatch loading state before and after fetch
 * - Dispatch fetched rules array to data/setRules
 * - One-time query, no subscription (rules change infrequently)
 *
 * CRITICAL: the `/rules` route default-excludes `kind: 'bypass'` rules when the
 * request carries no explicit `kind` clause (TV1-1648 / SDD §4). Bypass rows are
 * never rendered, but the suppression calculation in `resolveRulesForScope`
 * needs them to know which targeted rules a bypass rule cancels out. We opt out
 * of the default exclusion by passing an explicit `kind` OR-clause, mirroring the
 * filter+select pattern in `findRulesToExecute` (rules-utils.ts).
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { RulesReadmodelType } from '@bett3r-dev/teselly-domain';
import { AutomationSharedState } from '../state';
import { AutomationSharedCommand } from '../commands';

/**
 * Explicit `kind` clause: match both ordinary rules (`kind` absent) and bypass
 * rules. Without this, the `/rules` route silently drops bypass rows.
 */
const RULES_FILTER: NonNullable<Parameters<ClientLibraryType['queries']['Rules']>[0]>['filter'] = [
  {
    OR: [
      { field: 'kind', op: 'eq', value: 'bypass' },
      { field: 'kind', op: 'notExists' }
    ]
  }
];

/**
 * Fields consumed by the shared store: the navbar summary selectors
 * (`enabled`, `categories`, `hierarchyLevel`) and the scope-suppression
 * calculation in `resolveRulesForScope` (`hierarchy`, `hierarchyEntityIds`,
 * `overrideGroups`, `kind`, `targets`, `ruleDefinitionId`, `ruleId`). `name`,
 * `description`, `ruleDefinition` and `triggeringEvents` are retained for any
 * downstream UI that reads the shared rules array.
 */
const RULES_SELECT: ( keyof RulesReadmodelType )[] = [
  'id',
  'name',
  'description',
  'ruleDefinition',
  'ruleDefinitionId',
  'ruleId',
  'kind',
  'targets',
  'enabled',
  'categories',
  'triggeringEvents',
  'hierarchy',
  'hierarchyLevel',
  'hierarchyEntityIds',
  'overrideGroups'
];

export const createRulesQueryPolicy = (
  backend: ClientLibraryType
): Policy<AutomationSharedState, AutomationSharedCommand> => ({
  name: 'automation-shared-rules-query',

  init: ({ dispatch }) => {
    ( async () => {
      dispatch(
        { type: 'automation/shared/loading/setRulesLoading', payload: { isLoading: true }},
        { source: 'api' }
      );

      try {
        const rules = await backend.queries.Rules({
          filter: RULES_FILTER,
          select: RULES_SELECT
        });

        dispatch(
          { type: 'automation/shared/data/setRules', payload: { rules: rules || []}},
          { source: 'api' }
        );
      } catch ( error ) {
        console.error( '[automation-shared] Rules query error:', error );
        throw error;
      } finally {
        dispatch(
          { type: 'automation/shared/loading/setRulesLoading', payload: { isLoading: false }},
          { source: 'api' }
        );
      }
    })();

    // No cleanup needed for one-time fetch
    return undefined;
  }
});
